import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import './ComplianceTracker.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ComplianceTracker() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [urgentItems, setUrgentItems] = useState([]);
  const [selectedISP, setSelectedISP] = useState(null);
  const [ispChecklist, setIspChecklist] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);

  const commonISPs = [
    'Gmail',
    'Outlook',
    'Yahoo',
    'ProtonMail',
    'Apple Mail',
    'Office365'
  ];

  useEffect(() => {
    fetchComplianceData();
  }, [token]);

  const fetchComplianceData = async () => {
    try {
      setLoading(true);
      const [summaryRes, urgentRes, reportRes] = await Promise.all([
        axios.get(`${API_BASE}/compliance/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/compliance/urgent`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/compliance/report`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSummary(summaryRes.data.summary);
      setUrgentItems(urgentRes.data.items || []);
      setComplianceReport(reportRes.data);
    } catch (error) {
      toast.error('Error loading compliance data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchISPChecklist = async (isp) => {
    try {
      const response = await axios.get(
        `${API_BASE}/compliance/isp/${encodeURIComponent(isp)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIspChecklist(response.data);
      setSelectedISP(isp);
    } catch (error) {
      toast.error(`Error loading ${isp} checklist`);
    }
  };

  const updateComplianceStatus = async (requirementId, status) => {
    try {
      await axios.put(
        `${API_BASE}/compliance/requirements/${requirementId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Requirement marked as ${status}`);

      // Refresh data
      if (selectedISP) {
        await fetchISPChecklist(selectedISP);
      }
      await fetchComplianceData();
    } catch (error) {
      toast.error('Error updating compliance status');
    }
  };

  if (loading) {
    return <div className="compliance-loading">Loading compliance data...</div>;
  }

  return (
    <div className="compliance-tracker">
      {/* Header */}
      <div className="compliance-header">
        <h1>📋 Compliance Tracker</h1>
        <p>Track your implementation progress for email standards and ISP requirements</p>
      </div>

      {/* Overview Stats */}
      {summary && (
        <div className="compliance-overview">
          <div className="compliance-stats">
            <div className="stat-card primary">
              <div className="stat-value">{summary.total_requirements || 0}</div>
              <div className="stat-label">Total Requirements</div>
            </div>

            <div className="stat-card success">
              <div className="stat-value">{summary.completed || 0}</div>
              <div className="stat-label">Completed</div>
            </div>

            <div className="stat-card warning">
              <div className="stat-value">{summary.in_progress || 0}</div>
              <div className="stat-label">In Progress</div>
            </div>

            <div className="stat-card alert">
              <div className="stat-value">{summary.critical_pending || 0}</div>
              <div className="stat-label">Critical Pending</div>
            </div>

            <div className="stat-card danger">
              <div className="stat-value">{summary.overdue || 0}</div>
              <div className="stat-label">Overdue</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-label">
              <span>Overall Completion Progress</span>
              <span>{summary.completion_percentage?.toFixed(1) || 0}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${summary.completion_percentage || 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Urgent Section */}
      {urgentItems && urgentItems.length > 0 && (
        <div className="urgent-section">
          <h2>🚨 Urgent Items</h2>
          <div className="urgent-list">
            {urgentItems.map((item) => (
              <div
                key={item.id}
                className={`urgent-item urgency-${item.urgency_status}`}
              >
                <div className="urgent-header">
                  <div className="urgent-info">
                    <span className="isp-badge">{item.isp}</span>
                    <span className="urgent-label">
                      {item.urgency_status === 'overdue' ? '🚨 OVERDUE' : '⏰ DUE SOON'}
                    </span>
                    {item.days_remaining !== null && (
                      <span className="days-left">
                        {item.days_remaining <= 0
                          ? '⏱️ Today!'
                          : `${item.days_remaining} days left`}
                      </span>
                    )}
                  </div>
                  <span
                    className="status-badge"
                    style={{
                      background:
                        item.status === 'completed' ? '#e8f5e9' : '#fff5f5'
                    }}
                  >
                    {item.status || 'Not Started'}
                  </span>
                </div>

                <div className="urgent-requirement">
                  <strong>{item.requirement}</strong>
                  <p>{item.update_title}</p>
                  {item.deadline && (
                    <p style={{ fontSize: '12px', color: '#999' }}>
                      Deadline: {new Date(item.deadline).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="urgent-actions">
                  {item.status !== 'in_progress' && (
                    <button
                      className="btn-small btn-primary"
                      onClick={() =>
                        updateComplianceStatus(item.id, 'in_progress')
                      }
                    >
                      Start Work
                    </button>
                  )}
                  {item.status !== 'completed' && (
                    <button
                      className="btn-small btn-success"
                      onClick={() => updateComplianceStatus(item.id, 'completed')}
                    >
                      Mark Done
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ISP Checklists */}
      <div className="isp-checklists-section">
        <h2>✅ ISP-Specific Checklists</h2>

        <div className="isp-buttons">
          {commonISPs.map((isp) => (
            <button
              key={isp}
              className={`isp-btn ${selectedISP === isp ? 'active' : ''}`}
              onClick={() => fetchISPChecklist(isp)}
            >
              {isp}
            </button>
          ))}
        </div>

        {/* ISP Checklist Display */}
        {ispChecklist && selectedISP && (
          <div className="isp-checklist">
            <div className="checklist-header">
              <h3>{selectedISP} Requirements</h3>
              <div className="checklist-stats">
                <div className="stat total">
                  {ispChecklist.summary.total} total
                </div>
                <div className="stat completed">
                  {ispChecklist.summary.completed} completed
                </div>
                <div className="stat in-progress">
                  {ispChecklist.summary.in_progress} in progress
                </div>
                <div className="stat not-started">
                  {ispChecklist.summary.not_started} pending
                </div>
              </div>
            </div>

            <div className="checklist-items">
              {ispChecklist.checklist && ispChecklist.checklist.length > 0 ? (
                ispChecklist.checklist.map((item) => (
                  <div
                    key={item.id}
                    className={`checklist-item status-${item.status || 'not_started'}`}
                  >
                    <div className="item-header">
                      <input
                        type="checkbox"
                        checked={item.status === 'completed'}
                        onChange={() =>
                          updateComplianceStatus(
                            item.id,
                            item.status === 'completed'
                              ? 'in_progress'
                              : 'completed'
                          )
                        }
                      />
                      <div className="item-title">
                        <strong>{item.requirement}</strong>
                        {item.deadline && (
                          <span className="deadline">
                            Deadline: {new Date(item.deadline).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {item.impact_level && (
                        <span className={`impact-badge ${item.impact_level}`}>
                          {item.impact_level}
                        </span>
                      )}
                    </div>

                    <div className="item-details">
                      {item.update_title && (
                        <p>
                          <strong>Update:</strong> {item.update_title}
                        </p>
                      )}
                      {item.category && (
                        <p>
                          <span className="category">{item.category}</span>
                        </p>
                      )}

                      {item.implementation_steps && (
                        <details className="implementation-details">
                          <summary>📋 Implementation Steps</summary>
                          <ol>
                            {(() => {
                              try {
                                const steps = JSON.parse(
                                  item.implementation_steps
                                );
                                return steps.map((step, idx) => (
                                  <li key={idx}>{step}</li>
                                ));
                              } catch {
                                return (
                                  <li>{item.implementation_steps}</li>
                                );
                              }
                            })()}
                          </ol>
                        </details>
                      )}

                      {item.guide_url && (
                        <a
                          href={item.guide_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="guide-link"
                        >
                          📖 View Implementation Guide
                        </a>
                      )}
                    </div>

                    <div className="item-actions">
                      {item.status !== 'in_progress' && (
                        <button
                          className="btn-tiny primary"
                          onClick={() =>
                            updateComplianceStatus(item.id, 'in_progress')
                          }
                        >
                          Start
                        </button>
                      )}
                      {item.status !== 'completed' && (
                        <button
                          className="btn-tiny success"
                          onClick={() =>
                            updateComplianceStatus(item.id, 'completed')
                          }
                        >
                          Complete
                        </button>
                      )}
                      {item.status === 'completed' && (
                        <span className="status-badge completed">✓ Done</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No requirements found for {selectedISP}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compliance Report */}
      {complianceReport && (
        <div className="compliance-report" style={{ marginTop: '40px' }}>
          <h2>📊 Compliance Report by ISP</h2>
          <div style={{ overflowX: 'auto', marginTop: '20px' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: '#fff',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>ISP</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Total</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>✓ Completed</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>⏳ Active</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Not Started</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Progress</th>
                </tr>
              </thead>
              <tbody>
                {complianceReport.by_isp &&
                  complianceReport.by_isp.map((row) => (
                    <tr key={row.isp} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>{row.isp}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {row.total_items}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#2e7d32' }}>
                        {row.completed_items}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#f57c00' }}>
                        {row.active_items}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        {row.not_started}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{
                          background: '#eee',
                          borderRadius: '4px',
                          height: '24px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            background: '#667eea',
                            height: '100%',
                            width: `${row.completion_pct}%`,
                            transition: 'width 0.3s'
                          }}></div>
                        </div>
                        <small style={{ display: 'block', marginTop: '4px' }}>
                          {row.completion_pct.toFixed(0)}%
                        </small>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <strong>Overall Completion: {complianceReport.overall_stats?.completion_percentage?.toFixed(1) || 0}%</strong>
            <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
              {complianceReport.overall_stats?.completed_items} of {complianceReport.overall_stats?.total_items} requirements completed
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComplianceTracker;
